/**
 * Brand theming for public embed pages (form embed, reviews widget).
 *
 * Generated websites embed these pages in iframes and pass the site's brand
 * colors as query params so the widgets match the site instead of ApexOS:
 *
 *   /forms/embed/{id}?primary=7C3AED&bg=transparent
 *   /widget/reviews/{id}?primary=7C3AED&bg=transparent
 *
 * Params (all optional, hex WITHOUT the # prefix):
 *   primary — brand color for buttons/accents (overrides Joy primary tokens)
 *   text    — base text color
 *   bg      — page background hex, or the literal string "transparent"
 *             (pair with allowtransparency on the embedding iframe)
 */
export function applyEmbedBrandTheme(): void {
  const params = new URLSearchParams(window.location.search);

  const hexParam = (key: string): string | null => {
    const value = params.get(key);
    return value && /^[0-9a-fA-F]{6}$/.test(value) ? `#${value}` : null;
  };

  const primary = hexParam('primary');
  const text = hexParam('text');
  const bgRaw = params.get('bg');
  const bg = bgRaw === 'transparent' ? 'transparent' : bgRaw && /^[0-9a-fA-F]{6}$/.test(bgRaw) ? `#${bgRaw}` : null;

  if (document.head.querySelector('style[data-embed-brand-theme]')) return;

  // Always neutralize the app shell's full-height rules on embed pages:
  // html/body/#root carry height:100% from the SPA's global CSS, which made
  // documentElement.scrollHeight track the IFRAME height instead of the
  // content height — the embed then grew a little on every resize report
  // (the runaway bottom-whitespace bug).
  const rules: string[] = [
    'html, body, #root { height: auto !important; min-height: 0 !important; }',
  ];
  if (primary) {
    // Override Joy UI's primary palette tokens so solid buttons, focus
    // rings, links, and radio/checkbox accents pick up the site's brand.
    rules.push(
      `:root, [data-joy-color-scheme] {` +
        `--joy-palette-primary-solidBg:${primary};` +
        `--joy-palette-primary-solidHoverBg:color-mix(in srgb, ${primary} 85%, #000);` +
        `--joy-palette-primary-solidActiveBg:color-mix(in srgb, ${primary} 72%, #000);` +
        `--joy-palette-primary-outlinedColor:${primary};` +
        `--joy-palette-primary-outlinedBorder:${primary};` +
        `--joy-palette-primary-plainColor:${primary};` +
        `--joy-palette-primary-500:${primary};` +
        `--joy-palette-focusVisible:${primary};` +
      `}`,
    );
  }
  if (bg) rules.push(`html, body { background: ${bg} !important; }`);
  if (text) rules.push(`body { color: ${text}; }`);

  const style = document.createElement('style');
  style.setAttribute('data-embed-brand-theme', '');
  style.textContent = rules.join('\n');
  document.head.appendChild(style);
}

/**
 * Report this embed's rendered height (and optional item count) to the
 * parent page so the embedding iframe can auto-size — and, for reviews,
 * auto-hide when there is nothing to show. Returns a cleanup function.
 *
 * Height is measured from the `#apexos-embed-content` wrapper (never from
 * documentElement, whose scrollHeight follows the iframe's own height and
 * created a feedback loop that grew the iframe forever).
 */
export function reportEmbedState(
  message: { type: 'apexos-form-resize'; formId: string } | { type: 'apexos-reviews-state'; widgetId: string; count: number },
): () => void {
  if (window.parent === window) return () => undefined;

  let lastHeight = -1;
  const send = () => {
    const content = document.getElementById('apexos-embed-content');
    const height = Math.ceil(
      content ? content.getBoundingClientRect().height : document.body.scrollHeight,
    );
    if (Math.abs(height - lastHeight) < 4 && lastHeight !== -1) return;
    lastHeight = height;
    window.parent.postMessage({ ...message, height }, '*');
  };

  send();
  const observer = new ResizeObserver(send);
  observer.observe(document.getElementById('apexos-embed-content') || document.body);
  window.addEventListener('resize', send);
  return () => {
    observer.disconnect();
    window.removeEventListener('resize', send);
  };
}
